import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  Mail,
  Key,
  Eye,
  EyeOff,
  Shield,
  Trash2,
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  Check,
  Loader2,
} from 'lucide-react';
import { Alert, Input } from '@/components/ui';
import { SpotlightCard } from '@/components/verdict/SpotlightCard';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import type { useAuth } from '@/contexts/AuthContext';

interface ProfileTabProps {
  user: ReturnType<typeof useAuth>['user'];
  refreshUser: () => Promise<void>;
  logout: () => void;
}

export function ProfileTab({ user, refreshUser, logout }: ProfileTabProps) {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showDangerZone, setShowDangerZone] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false, delete: false });
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [formData, setFormData] = useState({ firstName: '', lastName: '' });
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [deleteData, setDeleteData] = useState({ password: '', confirmation: '' });

  useEffect(() => {
    if (user) {
      setFormData({ firstName: user.first_name || '', lastName: user.last_name || '' });
    }
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage(null);
    try {
      await api.updateProfile({
        first_name: formData.firstName || undefined,
        last_name: formData.lastName || undefined,
      });
      await refreshUser();
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to update profile' });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    if (passwordData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'New password must be at least 6 characters' });
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }
    setIsPasswordLoading(true);
    try {
      await api.changePassword({
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
      });
      setMessage({ type: 'success', text: 'Password changed successfully!' });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordForm(false);
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to change password' });
    } finally {
      setIsPasswordLoading(false);
    }
  };

  const handleDeleteAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    if (deleteData.confirmation !== 'DELETE') {
      setMessage({ type: 'error', text: 'Please type DELETE to confirm account deletion' });
      return;
    }
    if (!deleteData.password) {
      setMessage({ type: 'error', text: 'Please enter your password to confirm' });
      return;
    }
    setIsDeleteLoading(true);
    try {
      await api.deleteAccount({ password: deleteData.password, confirmation: deleteData.confirmation });
      logout();
      navigate('/', { replace: true });
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to delete account' });
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const inputClassName = 'bg-surface-light/50 border-border-subtle focus:border-verdict-primary/40 text-text-primary placeholder-text-muted';

  return (
    <div className="space-y-6">
      {message && (
        <Alert variant={message.type} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
      {/* Personal Information */}
      <SpotlightCard className="rounded-2xl" contentClassName="p-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-gradient-to-br from-verdict-primary/20 to-verdict-primary/5 rounded-xl border border-verdict-primary/15 flex-shrink-0">
            <User className="w-5 h-5 text-verdict-primary" />
          </div>
          <div>
            <h2 className="text-lg font-serif font-semibold text-text-primary leading-tight">Personal Information</h2>
            <p className="text-xs text-text-muted mt-0.5">Update your name and account details</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 mt-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              name="firstName"
              label="First Name"
              placeholder="John"
              value={formData.firstName}
              onChange={handleChange}
              leftIcon={<User className="w-4 h-4" />}
              className={inputClassName}
            />
            <Input
              name="lastName"
              label="Last Name"
              placeholder="Doe"
              value={formData.lastName}
              onChange={handleChange}
              className={inputClassName}
            />
          </div>

          {/* Email - read-only stat card style */}
          <div className="rounded-xl bg-surface-light/50 border border-border-subtle p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <Mail className="w-4 h-4 text-text-secondary" />
              <span className="text-[11px] font-medium text-text-secondary uppercase tracking-wider">Email</span>
            </div>
            <p className="text-sm text-text-primary break-all">{user?.email}</p>
            <p className="text-[11px] text-text-muted mt-1">Cannot be changed</p>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={cn(
              'px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center gap-2',
              'bg-gradient-to-r from-verdict-primary to-verdict-primary-hover text-black',
              'hover:shadow-[0_4px_20px_rgba(212,165,116,0.3)]',
              isLoading && 'opacity-70 cursor-not-allowed'
            )}
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Save Changes
          </button>
        </form>
      </SpotlightCard>

      {/* Security */}
      <SpotlightCard className="rounded-2xl" contentClassName="p-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-gradient-to-br from-verdict-primary/20 to-verdict-primary/5 rounded-xl border border-verdict-primary/15 flex-shrink-0">
            <Shield className="w-5 h-5 text-verdict-primary" />
          </div>
          <div>
            <h2 className="text-lg font-serif font-semibold text-text-primary leading-tight">Security</h2>
            <p className="text-xs text-text-muted mt-0.5">Manage your password</p>
          </div>
        </div>

        {!showPasswordForm ? (
          <button
            onClick={() => setShowPasswordForm(true)}
            className={cn(
              'w-full flex items-center justify-between px-4 py-3.5 rounded-xl mt-4 transition-all duration-200',
              'bg-surface-light/50 border border-border-subtle hover:bg-surface-light hover:border-border-subtle group'
            )}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-surface-light rounded-lg group-hover:bg-surface-raised transition-colors">
                <Key className="w-4 h-4 text-text-secondary" />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-text-primary">Password</p>
                <p className="text-xs text-text-muted">Change your account password</p>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-text-muted group-hover:text-text-secondary group-hover:translate-x-0.5 transition-all" />
          </button>
        ) : (
          <form onSubmit={handlePasswordChange} className="space-y-4 mt-4">
            <div className="relative">
              <Input
                type={showPasswords.current ? 'text' : 'password'}
                label="Current Password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                leftIcon={<Key className="w-4 h-4" />}
                className={inputClassName}
                required
              />
              <button
                type="button"
                className="absolute right-3 top-9 text-text-muted hover:text-text-primary transition-colors"
                onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
              >
                {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div className="relative">
              <Input
                type={showPasswords.new ? 'text' : 'password'}
                label="New Password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                leftIcon={<Key className="w-4 h-4" />}
                className={inputClassName}
                required
              />
              <button
                type="button"
                className="absolute right-3 top-9 text-text-muted hover:text-text-primary transition-colors"
                onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
              >
                {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div className="relative">
              <Input
                type={showPasswords.confirm ? 'text' : 'password'}
                label="Confirm New Password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                leftIcon={<Key className="w-4 h-4" />}
                className={inputClassName}
                required
              />
              <button
                type="button"
                className="absolute right-3 top-9 text-text-muted hover:text-text-primary transition-colors"
                onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
              >
                {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="submit"
                disabled={isPasswordLoading}
                className={cn(
                  'px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center gap-2',
                  'bg-gradient-to-r from-verdict-primary to-verdict-primary-hover text-black',
                  'hover:shadow-[0_4px_20px_rgba(212,165,116,0.3)]',
                  isPasswordLoading && 'opacity-70 cursor-not-allowed'
                )}
              >
                {isPasswordLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Update Password
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowPasswordForm(false);
                  setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                }}
                className={cn(
                  'px-5 py-3 rounded-xl text-sm font-medium transition-all duration-200',
                  'bg-surface-light border border-border-subtle text-text-primary',
                  'hover:bg-surface-raised hover:border-border-subtle hover:text-text-primary'
                )}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </SpotlightCard>
      </div>

      {/* Danger Zone - de-emphasized, collapsed by default */}
      <div className="pt-6 border-t border-border-subtle">
        <button
          onClick={() => setShowDangerZone(!showDangerZone)}
          className="flex items-center gap-2 text-xs text-text-muted hover:text-text-secondary transition-colors"
        >
          <AlertTriangle className="w-3.5 h-3.5" />
          <span className="uppercase tracking-wider font-medium">Danger Zone</span>
          <ChevronDown className={cn('w-3 h-3 transition-transform duration-200', showDangerZone && 'rotate-180')} />
        </button>

        {showDangerZone && (
          <div className="mt-4 rounded-xl border border-red-500/10 bg-red-500/[0.02] p-5 animate-fade-in">
            {!showDeleteConfirm ? (
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <Trash2 className="w-4 h-4 text-red-400/70 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-text-primary">Delete Account</p>
                    <p className="text-xs text-text-muted">Permanently delete your account and all data</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className={cn(
                    'px-4 py-2.5 rounded-xl text-xs font-medium transition-all duration-200',
                    'bg-red-500/[0.06] border border-red-500/15 text-red-400',
                    'hover:bg-red-500/10 hover:border-red-500/25 hover:text-red-300'
                  )}
                >
                  Delete Account
                </button>
              </div>
            ) : (
              <form onSubmit={handleDeleteAccount} className="space-y-4">
                <div className="flex items-start gap-3 p-3 bg-red-500/[0.06] border border-red-500/10 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="text-xs">
                    <p className="font-medium text-red-400">This action cannot be undone</p>
                    <p className="text-text-muted mt-1">
                      This will permanently delete your account, including all your preferences,
                      newsletter history, uploaded files, and subscription data.
                    </p>
                  </div>
                </div>

                <div className="relative">
                  <Input
                    type={showPasswords.delete ? 'text' : 'password'}
                    label="Enter your password to confirm"
                    value={deleteData.password}
                    onChange={(e) => setDeleteData(prev => ({ ...prev, password: e.target.value }))}
                    leftIcon={<Key className="w-4 h-4" />}
                    className={inputClassName}
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-9 text-text-muted hover:text-text-primary transition-colors"
                    onClick={() => setShowPasswords(prev => ({ ...prev, delete: !prev.delete }))}
                  >
                    {showPasswords.delete ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>

                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-2">
                    Type <code className="bg-surface-light px-1.5 py-0.5 rounded text-red-400 text-[11px]">DELETE</code> to confirm
                  </label>
                  <Input
                    value={deleteData.confirmation}
                    onChange={(e) => setDeleteData(prev => ({ ...prev, confirmation: e.target.value }))}
                    placeholder="DELETE"
                    className={inputClassName}
                    required
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="submit"
                    disabled={isDeleteLoading}
                    className={cn(
                      'px-5 py-2.5 rounded-xl text-xs font-semibold transition-all duration-200 flex items-center gap-2',
                      'bg-red-600 text-text-primary hover:bg-red-700',
                      isDeleteLoading && 'opacity-70 cursor-not-allowed'
                    )}
                  >
                    {isDeleteLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    Permanently Delete Account
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeleteData({ password: '', confirmation: '' });
                    }}
                    className={cn(
                      'px-4 py-2.5 rounded-xl text-xs font-medium transition-all duration-200',
                      'bg-surface-light border border-border-subtle text-text-primary',
                      'hover:bg-surface-raised hover:border-border-subtle hover:text-text-primary'
                    )}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
